import { ActivityNote, FormData } from "0.Mine/0.Components/CRM/Leads/sharedTypes";
import { Business } from "0.Mine/3.Interfaces/business";
import { WorkFlow } from "0.Mine/3.Interfaces/workFlow";

import { extractJsonFromString, UseCreateGeminiRequest } from "../AI/Gemini";

export const CreateWorkFlowWithAI = () => {
  const { generateWorkFlowWithGemini } = UseCreateGeminiRequest();

  const createFlow = async (business: Business, tone: string, goal: string) => {
    const randomNumber = Math.floor(Math.random() * 4) + 3;
    const response = await generateWorkFlowWithGemini(business, randomNumber, tone, goal);
    console.log(response);

    try {
      const data = extractJsonFromString(response);
      return data as WorkFlow;
    } catch (error) {
      console.error("Error parsing JSON:", error);
      return null;
    }
  };

  return { createFlow };
};

export function buildLeadAnalysisPrompt(lead: FormData, activities: ActivityNote[]): string {
  // Safe date formatting with fallbacks
  const fmt = (dateValue: any): string => {
    if (!dateValue) return "unknown date";

    try {
      // Handle string dates
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);

      // Check if valid date
      if (isNaN(date.getTime())) return "unknown date";

      return date.toISOString().split("T")[0];
    } catch (error) {
      console.error("Error formatting date:", error);
      return "unknown date";
    }
  };

  // Safe date comparison for sorting
  const safeCompareDate = (a: any, b: any): number => {
    try {
      // Convert to dates if they aren't already
      const dateA = a instanceof Date ? a : new Date(a);
      const dateB = b instanceof Date ? b : new Date(b);

      // Check if both dates are valid
      const isValidA = !isNaN(dateA.getTime());
      const isValidB = !isNaN(dateB.getTime());

      if (isValidA && isValidB) {
        return dateB.getTime() - dateA.getTime();
      } else if (isValidA) {
        return -1; // Valid dates come first
      } else if (isValidB) {
        return 1;
      } else {
        return 0; // Both invalid, consider equal
      }
    } catch (error) {
      console.error("Error comparing dates:", error);
      return 0;
    }
  };

  // Safely process activity notes
  let recent = "No recent activities";
  if (Array.isArray(activities) && activities.length > 0) {
    try {
      const recentActivities = [...activities]
        .sort((a, b) => safeCompareDate(a.date, b.date))
        .slice(0, 3)
        .map((n) => {
          const type = n.type || "interaction";
          const date = fmt(n.date);
          const title = n.title || "No details";

          return `${type} on ${date}: "${title}"`;
        })
        .filter(Boolean);

      if (recentActivities.length > 0) {
        recent = recentActivities.join("; ");
      }
    } catch (error) {
      console.error("Error processing activities:", error);
      // Fallback remains "No recent activities"
    }
  }

  // Extract lead data with fallbacks
  const vendorName = lead?.vendorName || "the vendor";
  const name = lead?.name || "the lead";
  const company = lead?.company || "their company";
  const value = lead?.value ? `$${lead.value}` : "unknown value";
  const probability = lead?.probability ? `${lead.probability}%` : "unknown probability";
  const lastContact = lead?.lastContact ? fmt(lead.lastContact) : "unknown last contact date";
  const notes = lead?.notes || "No additional notes";

  // Build the prompt with all our fallbacks in place
  return [
    `You're a casual Gen Z strategist helping ${vendorName}.`,
    `Review this lead (${name} @ ${company}, value ${value}, win ${probability}, last contact ${lastContact}).`,
    `Notes: ${notes}. Recent: ${recent}.`,
    `Identify one untapped insight or angle that could boost conversion,`,
    `and give a high-impact tactical recommendation.`,
    `Use a friendly tone, and avoid jargon.`,
    `Be concise and actionable.`,
    `Most importantly, write it all in ITALIAN! i repeat, i want the response to be in ITALIAN!`,
    `Write in 50 words.`,
  ].join(" ");
}
